import { isAxiosError } from 'axios';
import api from '../../../lib/axios';
import { BaseResponse } from '../../../lib/api-contract';
import { MerchantPlanApiResponse } from './MerchantPlan.data';
export type BillingPlan = {
    code: string;
    name: string;
    monthlyPrice: number | null;
    annualPrice: number | null;
    features: string[];
};

type StripeCheckoutSessionRequest = {
    plan_code: string;
};

type StripeCheckoutSessionResponse = {
    checkout_url: string;
    session_id?: string;
};

type BillingPlanApiItem = {
    code?: string;
    plan_code?: string;
    name?: string;
    plan_name?: string;
    monthly_price?: number | string | null;
    annual_price?: number | string | null;
    price_monthly?: number | string | null;
    price_annual?: number | string | null;
    features?: string[] | null;
};

const toNullableNumber = (value: number | string | null | undefined): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const normalizeBillingPlan = (plan: BillingPlanApiItem): BillingPlan => {
    const code = (plan.code || plan.plan_code || '').trim();
    const name = (plan.name || plan.plan_name || code || 'Unknown').trim();

    return {
        code,
        name,
        monthlyPrice: toNullableNumber(plan.monthly_price ?? plan.price_monthly),
        annualPrice: toNullableNumber(plan.annual_price ?? plan.price_annual),
        features: Array.isArray(plan.features) ? plan.features.filter((item): item is string => typeof item === 'string') : [],
    };
};

const getResponseMessage = (value: unknown, fallback: string): string => {
    if (typeof value !== 'object' || value === null) {
        return fallback;
    }

    const record = value as Record<string, unknown>;

    return typeof record.user_message === 'string'
        ? record.user_message
        : typeof record.message === 'string'
            ? record.message
            : fallback;
};

export const getMerchantPlanErrorMessage = (error: unknown, fallback: string): string => {
    if (isAxiosError(error)) {
        return getResponseMessage(error.response?.data, error.message || fallback);
    }

    return error instanceof Error ? error.message : fallback;
};

export const fetchBillingPlans = async (signal?: AbortSignal): Promise<BillingPlan[]> => {
    const response = await api.get<BaseResponse<BillingPlanApiItem[] | null>>('/v1/billing/plans', { signal });
    const result = response.data;

    if (!result.success || result.http_request_status !== 200) {
        throw new Error(result.user_message || result.message || 'Failed to fetch billing plans');
    }

    const plans = Array.isArray(result.data) ? result.data : [];
    return plans.map(normalizeBillingPlan);
};

export const fetchMerchantPlan = async (): Promise<MerchantPlanApiResponse> => {
    // TODO: Implement when API is available
    // const response = await api.get<BaseResponse<MerchantPlanApiResponse>>('/v1/merchant/local-merchant/plan');
    // return response.data.data;
    
    // Mock data for now
    return {
        current_plan: 'free',
        subscription_status: 'active',
    };
};

export const upgradePlan = async (
    planType: 'basic' | 'premium'
): Promise<StripeCheckoutSessionResponse> => {
    const request: StripeCheckoutSessionRequest = {
        plan_code: planType,
    };

    const response = await api.post<BaseResponse<StripeCheckoutSessionResponse | null>>(
        '/v1/billing/stripe/checkout-session',
        request
    );

    const result = response.data;

    if (!result.success || result.http_request_status !== 200 || !result.data?.checkout_url) {
        throw new Error(result.user_message || result.message || 'Failed to create checkout session');
    }

    return result.data;
};
